/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 *
 */

/**
 * @module bulb-attached-file/bulbattacedfileui
 */

import { Plugin, ButtonView, type Locale } from 'ckeditor5';
import attachedFileIcon from '../../assets/block_toolbar_icons/file-regular.svg';
import { assetInsertLocation, cancelBusyIndicator } from '../utils';

export default class BulbAttachedFileUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbAttachedFileUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;

    editor.ui.componentFactory.add('bulbInsertAttachedFile', (locale: Locale): ButtonView => {
      // The state of the button will be bound to the widget command.
      const command = editor.commands.get('insertAttachedFile');

      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      buttonView.set({
        label: t('content_block_type_FILE'),
        icon: attachedFileIcon,
        tooltip: true,
        withText: true,
      });

      // Callback executed once the button is clicked.
      buttonView.on('execute', (event) => {
        const openAssetLibraryEvent = new CustomEvent('openAssetLibraryFileType', {
          detail: {
            event,
            eventFiredFromEditor: true,
          },
        });
        window.dispatchEvent(openAssetLibraryEvent);
      });

      // Listen for the event that is going to be fired from our client that the upload was successful.
      window.addEventListener('assetLibraryFileResponse', (event) => {
        // Handle the attached file block differently depending on what type it is :
        const attachFileBlockType = event.detail.contentBlock.blockType;
        assetInsertLocation(this.editor);
        if (attachFileBlockType === 'ATTACHED_FILE') {
          editor.execute('insertAttachedFile', event.detail.contentBlock);
        } else if (attachFileBlockType === 'ATTACHED_FILE_EMBED' || attachFileBlockType === 'EMBED') {
          editor.execute('insertEmbed', event.detail.contentBlock);
        }
        cancelBusyIndicator(editor);
      });

      // Bind the state of the button to the command.
      buttonView.bind('isOn', 'isEnabled').to(command as any, 'value', 'isEnabled');
      return buttonView;
    });
  }
}
